import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./navbar";
import Footer from "./footer";
import "../index.css";

const BASE_URL = process.env.REACT_APP_DATA_FEEDER;
  const token = window.sessionStorage.getItem("jwtToken");

const apiMap = {
  quarterly: {
    "Latest Quarter Financial Data": "/fetchSymbolFinancials/quarterly",
    "Symbol Key Metrics Quarterly": "/sourceSymbolKeyMetrics/quarterly",
    "Latest Symbol Financial Ratios Data":
      "/fetchLatestSymbolFinancialRatios/quarterly",
    "Latest Symbol Income Statement": "/latest/incomestatement/quarterly",
    "Symbol Income Statement Growth Latest Quarterly":
      "/symbolIncomeStatementGrowth/quarterly",
    "Balanced Sheet Statement Latest Quarterly":
      "/balancedSheetStatementLatestQuarterly",
  },
  yearly: {
    "Latest Yearly Financial Data": "/fetchSymbolFinancials/yearly",
    "Symbol Key Metrics Yearly": "/sourceSymbolKeyMetrics/yearly",
    "Latest Symbol Financial Ratios Data Yearly":
      "/fetchLatestSymbolFinancialRatio/yearly",
    "Symbol Income Statement Yearly": "/latest/incomestatement/yearly",
    "Symbol Income Statement Growth Yearly":
      "/symbolIncomeStatementGrowthLatestYearly",
    "Balanced Sheet Statement Yearly": "/balancedSheetStatementLatestYearly",
    "Balanced Sheet Statement Growth Yearly":
      "/balancedSheetStatementGrowthYearlyLatest",
    "Cash Flow Statement Yearly": "/cashFlowStatementYearlyLatest",
    "Cash Flow Growth Statement Yearly": "/cashFlowGrowthStatementYearlyLatest",
  },
  aggregationCalculation: {
    "Update OPM Ratio Quarterly": "/updateOPMRatioQuarterly",
    "Transform Stock Revenue Growth Quarterly For Financial Services":
      "/transformStockRevenueGrowthQuarterlyForFinancialServices",
    "Calculate Sector Growth Quarterly Latest":
      "/calculateQuarterlySectorGrowthLatest",
    "Calculate Market Share Quarterly Latest":
      "/calculateQuarterlyMarketShareLatest",
    "Update OPM Ratio Yearly": "/updateOPMRatioYearly",
    "Update ROCE Yearly": "/updateROCEYearly",
    "Transform Stock Revenue Growth Yearly For Financial Services":
      "/transformStockRevenueGrowthYearlyForFinancialServices",
    "Calculate Sector Growth Yearly Latest":
      "/calculateYearlySectorGrowthLatest",
    "Calculate Market Share Yearly Latest": "/calculateYearlyMarketShareLatest",
  },
};

const monthlyTransformationList = [
  {
    name: "Transform Fundamental Ratio Yearly",
    url: "/transformStockFundamentalRatioYearlyDataIntoMonthly",
    period: "yearly",
  },
  {
    name: "Transform Key Metrics Yearly",
    url: "/transformStockFundamentalKeyMetricsYearlyDataIntoMonthly",
    period: "yearly",
  },
  {
    name: "Transform Growth Yearly",
    url: "/transformStockGrowthYearlyDataIntoMonthly",
    period: "yearly",
  },
  {
    name: "Transform Key Metrics Quarterly",
    url: "/transformStockFundamentalKeyMetricsQuarterlyDataIntoMonthly",
    period: "quarterly",
  },
  {
    name: "Transform Ratio Quarterly",
    url: "/transformStockFundamentalRatioQuarterlyDataIntoMonthly",
    period: "quarterly",
  },
  {
    name: "Transform Growth Quarterly",
    url: "/transformStockGrowthQuarterlyDataIntoMonthly",
    period: "quarterly",
  },
];

const quarterlySchedulers = Object.keys(apiMap.quarterly);
const yearlySchedulers = Object.keys(apiMap.yearly);
const quarterlyAggSchedulers = [
  "Update OPM Ratio Quarterly",
  "Transform Stock Revenue Growth Quarterly For Financial Services",
  "Calculate Sector Growth Quarterly Latest",
  "Calculate Market Share Quarterly Latest",
  "Copy Previous Quarter Market Sector Leaders Latest",
];
const yearlyAggSchedulers = [
  "Update OPM Ratio Yearly",
  "Update ROCE Yearly",
  "Transform Stock Revenue Growth Yearly For Financial Services",
  "Calculate Sector Growth Yearly Latest",
  "Calculate Market Share Yearly Latest",
];

const AdHocQuarterlyAndYearly = () => {
  const [stockInputs, setStockInputs] = useState({});
  const [latestInputs, setLatestInputs] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [monthYearInputs, setMonthYearInputs] = useState({});
  const [monthlyStockInputs, setMonthlyStockInputs] = useState({});

  const handleStockNameChange = (indexKey, value) => {
    setStockInputs({ ...stockInputs, [indexKey]: value });
  };

  const handleLatestChange = (indexKey, value) => {
    setLatestInputs({ ...latestInputs, [indexKey]: value });
  };

  const determinePeriod = (schedulerName, defaultPeriod) => {
    if (defaultPeriod === "aggregationCalculation") {
      return quarterlyAggSchedulers.includes(schedulerName)
        ? "quarterly"
        : "yearly";
    }
    return defaultPeriod;
  };

  const handleSourceClick = async (
    schedulerName,
    stockName,
    selectedPeriod,
    indexKey,
    latestValue, // true or false
  ) => {
    let endpoint = apiMap[selectedPeriod]?.[schedulerName];
    if (!endpoint && selectedPeriod === "aggregationCalculation") {
      endpoint = apiMap["aggregationCalculation"]?.[schedulerName];
    }

    if (!endpoint) {
      alert("No matching API endpoint found for this scheduler.");
      return;
    }

    let url = `${BASE_URL}${endpoint}`;

    const params = [];
    if (stockName && stockName.trim() !== "") {
      params.push(`stockName=${stockName.trim()}`);
    }

    // always append latest=true/false
    params.push(`latest=${latestValue}`);

    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("API call failed");

      const data = await response.text();
      setModalMessage("Success: " + data);
      setShowModal(true);

      setStockInputs((prev) => ({ ...prev, [indexKey]: "" }));
    } catch (error) {
      console.error("Error:", error);
      setModalMessage(`Error: ${schedulerName} failed.`);
      setShowModal(true);
    }
  };

  const handleMonthYearChange = (index, value) => {
    setMonthYearInputs((prev) => ({ ...prev, [index]: value }));
  };

  const handleMonthlyStockNameChange = (index, value) => {
    setMonthlyStockInputs((prev) => ({ ...prev, [index]: value }));
  };

  const handleMonthlyTransformationClick = async (endpoint, index) => {
    const monthYear = monthYearInputs[`Monthly-${index}`] || "";
    const stockName = monthlyStockInputs[`Monthly-${index}`] || "";

    if (!monthYear.trim() && !stockName.trim()) {
      alert("Please provide either Month-Year or Stock Name.");
      return;
    }

    let url = `${BASE_URL}${endpoint}`;
    const params = [];

    if (monthYear.trim()) params.push(`monthYear=${monthYear.trim()}`);
    if (stockName.trim()) params.push(`stockName=${stockName.trim()}`);

    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("API call failed");
      const data = await response.text();
      setModalMessage("Success: " + data);
      setShowModal(true);
    } catch (error) {
      console.error("Error:", error);
      setModalMessage(`Error: ${endpoint} failed.`);
      setShowModal(true);
    }
  };

  const renderTable = (dataList, label, defaultPeriod) => {
    const isSourceSection =
      label.includes("Source Quarterly") ||
      label.includes("Source Yearly") ||
      label.includes("Aggregation Calculation");

    return (
      <div className="container-fluid" style={{ paddingTop: "20px" }}>
        <h4 className="fw-bold text-center">{label}</h4>
        <div className="table-responsive">
          <table className="table table-striped align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: "40px" }}>ID</th>
                <th style={{ minWidth: "280px" }}>Scheduler Name</th>
                <th style={{ minWidth: "220px" }}>Stock Name</th>
                <th style={{ minWidth: "120px" }}>Period</th>
                {isSourceSection && (
                  <th style={{ minWidth: "120px" }}>Latest</th>
                )}
                <th style={{ minWidth: "150px" }}>Source Data</th>
              </tr>
            </thead>
            <tbody>
              {dataList.map((cron, index) => {
                const period = determinePeriod(cron, defaultPeriod);
                const indexKey = `${label}-${index}`;
                return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{cron}</td>
                    <td>
                      <input
                        className="form-control"
                        value={stockInputs[indexKey] || ""}
                        onChange={(e) =>
                          handleStockNameChange(indexKey, e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <select value={period} disabled className="form-select">
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </td>
                    {isSourceSection && (
                      <td>
                        <select
                          className="form-select"
                          value={latestInputs[indexKey] || "latest"}
                          onChange={(e) =>
                            handleLatestChange(indexKey, e.target.value)
                          }
                        >
                          <option value="latest">Latest</option>
                          <option value="completed">Completed</option>
                        </select>
                      </td>
                    )}
                    <td>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          const stock = stockInputs[indexKey];
                          const latestSelection =
                            latestInputs[indexKey] || "latest";

                          // map dropdown to boolean
                          const latest = latestSelection === "latest";

                          handleSourceClick(
                            cron,
                            stock,
                            defaultPeriod,
                            indexKey,
                            latest,
                          );
                        }}
                      >
                        Source Data
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <div className="main-content" style={{ paddingTop: "50px" }}>
        {renderTable(quarterlySchedulers, "Source Quarterly Data", "quarterly")}
        {renderTable(
          quarterlyAggSchedulers,
          "Quarterly Aggregation Calculation",
          "aggregationCalculation",
        )}
        {renderTable(yearlySchedulers, "Source Yearly Data", "yearly")}
        {renderTable(
          yearlyAggSchedulers,
          "Yearly Aggregation Calculation",
          "aggregationCalculation",
        )}
        {monthlyTransformationList.length > 0 && (
          <div className="container-fluid" style={{ paddingTop: "20px" }}>
            <h4 className="fw-bold text-center">Monthly Transformation</h4>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Transformation Name</th>
                    <th>Month-Year</th>
                    <th>Stock Name</th>
                    <th>Period</th>
                    <th style={{ paddingLeft: "30px" }}>Source Data</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyTransformationList.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td style={{ maxWidth: "200px" }}>{item.name}</td>
                      <td>
                        <input
                          style={{ width: "100%", maxWidth: "150px" }}
                          className="form-control"
                          value={monthYearInputs[`Monthly-${index}`] || ""}
                          onChange={(e) =>
                            handleMonthYearChange(
                              `Monthly-${index}`,
                              e.target.value,
                            )
                          }
                          placeholder="MMMYYYY"
                        />
                      </td>
                      <td>
                        <input
                          style={{ width: "100%", maxWidth: "200px" }}
                          className="form-control"
                          value={monthlyStockInputs[`Monthly-${index}`] || ""}
                          onChange={(e) =>
                            handleMonthlyStockNameChange(
                              `Monthly-${index}`,
                              e.target.value,
                            )
                          }
                          placeholder="Stock Name"
                        />
                      </td>
                      <td style={{ maxWidth: "100px" }}>
                        <select
                          value={item.period}
                          disabled
                          className="form-select"
                        >
                          <option value="quarterly">Quarterly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </td>
                      <td style={{ paddingLeft: "30px", maxWidth: "200px" }}>
                        <button
                          type="button"
                          class="btn btn-primary"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleMonthlyTransformationClick(item.url, index);
                          }}
                        >
                          Source Data
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: "block" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">API Response</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>{modalMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default AdHocQuarterlyAndYearly;
